// Verbatim ports of the public apps' result formatters.
//
// Each function below is the upstream function with its body kept as close to
// the original as TypeScript-in-a-different-repo allows (same names, same
// order, same strings) so that `diff` against the upstream file is mechanical.
// Styling is NOT ported — only what text appears, in which order, with which
// tone. When a public app changes its copy, update the port, the fixture
// expectation in model.test.mjs, and the "last synced" date.
//
// No runtime imports: this file is loaded as-is by `node --test`.

import type { TranslationData } from '../../Entries/entryTypes'

type Rec = Record<string, unknown>

// ─── Burla ou Não webapp — result bubbles ─────────────────────────────────────
// Upstream: fraud-mono-repo/webapp/src/components/post/Card1Chat.tsx
//           handleSubmit (the `msgs` builder) + ResultView. Last synced 2026-10-18.
// Omitted on purpose: the logged-in "Submeteste pela Nª vez 🥳" line (the count
// is the submitter's, unknown here) and the two buttons under the bubbles.

export type BonResultMessage = { text: string; status?: 'success' | 'error' }

/** What Card1Chat shows when the request itself throws. */
export const BON_SUBMIT_ERROR: BonResultMessage = { text: 'Não foi possível partilhar agora. Tenta de novo mais tarde.', status: 'error' }

export function buildBonResultMessages(result: Rec | null | undefined, postType: string | undefined, showSubmitResult = true): BonResultMessage[] {
  const msgs: BonResultMessage[] = []
  if (result?.classificationPending) {
    msgs.push({ text: 'A tua partilha foi recebida. A comunidade fica com mais contexto para decidir.', status: 'success' })
  } else if (postType !== 'call' && showSubmitResult) {
    const t = result?.translation as TranslationData | undefined
    const s = result?.is_scam === true ? 'error' : result?.is_scam === false ? 'success' : undefined
    if (t?.verdict_label) msgs.push({ text: t.verdict_label, status: s })
    if (t?.summary) msgs.push({ text: t.summary, status: s })
    if (t?.explanation) msgs.push({ text: t.explanation, status: s })
    t?.recommendations?.forEach((r: string) => msgs.push({ text: `• ${r}`, status: s }))
    if (!t) msgs.push({ text: 'Ainda não temos informação suficiente. Na dúvida, decide com calma e não avances sem confirmar.' })
  } else {
    msgs.push({ text: 'Obrigado pela partilha. Cada report pode ajudar a próxima pessoa.', status: 'success' })
  }
  return msgs
}

// ─── Shared helpers (É Legítimo and project-domain carry identical copies) ────

function getRecord(value: unknown): Rec | null {
  return value && typeof value === 'object' ? (value as Rec) : null
}

function unwrapBackendPayload(value: unknown): Rec | null {
  const outer = getRecord(value)
  if (!outer) return null

  const first = getRecord(outer.data) ?? outer
  return getRecord(first.data) ?? first
}

export type VerdictTone = 'danger' | 'safe' | 'warning' | 'error'

/** The label / message / bullets box both É Legítimo and the domain checker draw. */
export type VerdictBoxData = {
  label: string
  message: string
  items: string[]
  tone: VerdictTone
}

// ─── É Legítimo — verdict box ─────────────────────────────────────────────────
// Upstream: fraud-mono-repo/project-elegitimo/src/app/page.tsx
//           formatBackendVerdict + the submit-state strings around
//           pollClassificationStatus. Last synced 2026-10-18.

export function formatElegitimoVerdict(value: unknown): VerdictBoxData | null {
  const payload = unwrapBackendPayload(value)
  if (!payload) return null

  if (payload.classificationPending) {
    return {
      label: 'Análise em curso',
      message: 'Submissão recebida. O veredicto final ainda está em preparação.',
      items: [],
      tone: 'warning',
    }
  }

  // The classifier's plain-language block (verdict_label / summary / explanation /
  // recommendations, written for the person who received the message) beats the
  // operator-facing scam_claims when it is there.
  const translation = getRecord(payload.translation)
  if (translation) {
    const label = typeof translation.verdict_label === 'string' ? translation.verdict_label : ''
    const summary = typeof translation.summary === 'string' ? translation.summary : ''
    const explanation = typeof translation.explanation === 'string' ? translation.explanation : ''
    const recommendations = Array.isArray(translation.recommendations)
      ? translation.recommendations.map(String).filter(Boolean).slice(0, 5)
      : []
    const message = [summary, explanation].filter(Boolean).join(' ').trim()
    if (label || message) {
      return {
        label: label || (payload.is_scam === true ? 'Provável burla' : 'Resultado recebido'),
        message: message || 'A análise terminou.',
        items: recommendations,
        tone: payload.is_scam === true ? 'danger' : payload.is_scam === false ? 'safe' : 'warning',
      }
    }
  }

  const bkResult = getRecord(payload.bk_result)
  const claims = Array.isArray(payload.scam_claims) ? payload.scam_claims.map(String).filter(Boolean) : []
  const explanation =
    typeof payload.explanation === 'string' ? payload.explanation : typeof payload.message === 'string' ? payload.message : undefined
  const recipient =
    typeof bkResult?.beneficiario === 'string'
      ? bkResult.beneficiario
      : typeof bkResult?.paymentowner === 'string'
        ? bkResult.paymentowner
        : undefined
  const items = [recipient ? `Destinatário indicado: ${recipient}` : null, ...claims].filter(Boolean).map(String).slice(0, 5)
  const fallbackMessage = explanation ?? items[0]

  if (payload.is_scam === true) {
    return {
      label: 'Provável burla',
      message: fallbackMessage || 'O backend encontrou sinais fortes de fraude.',
      items,
      tone: 'danger',
    }
  }

  if (payload.is_scam === false || payload.status === 'found') {
    return {
      label: 'Parece legítima',
      message: fallbackMessage || 'Não foram encontrados sinais fortes de fraude nesta análise.',
      items,
      tone: 'safe',
    }
  }

  if (fallbackMessage || items.length > 0) {
    return {
      label: 'Resultado recebido',
      message: fallbackMessage ?? 'O serviço devolveu sinais adicionais.',
      items,
      tone: 'warning',
    }
  }

  return null
}

/** The box É Legítimo shows while polling, after the 4-minute poll window, and on a failed classification. */
export const ELEGITIMO_STATES = {
  submitting: { label: 'A analisar', message: 'Submissão recebida. A analisar a mensagem, pode demorar um ou dois minutos.', items: [], tone: 'warning' } satisfies VerdictBoxData,
  slow: 'Ainda a analisar. Está a demorar mais do que o habitual, mas a submissão ficou registada.',
  timeout: {
    label: 'Análise em curso',
    message: 'A análise ainda não terminou. A mensagem ficou registada e vai ser revista; volta a tentar daqui a alguns minutos.',
    items: [],
    tone: 'warning',
  } satisfies VerdictBoxData,
  failed: {
    label: 'Não foi possível analisar',
    message: 'Não conseguimos concluir a análise automática. A mensagem ficou registada e vai ser revista.',
    items: [],
    tone: 'error',
  } satisfies VerdictBoxData,
  /** formatBackendVerdict returned null for a done result. */
  doneNoVerdict: { label: 'Análise recebida', message: 'A análise terminou.', items: [], tone: 'safe' } satisfies VerdictBoxData,
}

// ─── Multibanco / IBAN / Is It Scam validators — "Resultado" list ─────────────
// Upstream: fraud-mono-repo/multibanco-validator/src/components/PaymentValidatorForm.tsx
//           messagesFromBackendResult + errorFromBackendResult; project-iban and
//           project-isitscam carry byte-identical copies. Last synced 2026-10-18.
// Feed it exactly what Hono returned: { success: true, data: <mbvalidate json> }.
// Note the formatter reads `r.payments_metadata` off the OUTER object and
// `payload.bk_result` — the stored mbvalidate result has `beneficiario` at the
// top level instead, so "Destinatário indicado" never renders in production.
// The port reproduces that; the widget footer says so.

export function validatorMessages(result: unknown): string[] {
  if (!result || typeof result !== 'object') return []
  const r = result as Rec
  const payload = (r.data && typeof r.data === 'object' ? r.data : r) as Rec
  if (payload.classificationPending) return []

  const explanation = payload.explanation as string | undefined
  const message = payload.message as string | undefined
  const paymentsMetadata = r.payments_metadata as Rec | undefined
  const entityNumber = paymentsMetadata?.entity_number as string | undefined
  const reference = paymentsMetadata?.reference as string | undefined
  const value = paymentsMetadata?.value as string | undefined
  const bkResult = payload.bk_result as Rec | undefined
  const beneficiario = (paymentsMetadata?.beneficiario ?? bkResult?.beneficiario ?? bkResult?.paymentowner) as string | undefined

  return [
    beneficiario ? `Destinatário indicado: ${beneficiario}` : null,
    entityNumber ? `Entidade: ${entityNumber}` : null,
    reference ? `Referência: ${reference}` : null,
    value ? `Valor: ${value}` : null,
    explanation ?? message ?? null,
  ].filter(Boolean) as string[]
}

export function validatorError(result: unknown): string | null {
  if (!result || typeof result !== 'object') return null
  const r = result as Rec
  const payload = (r.data && typeof r.data === 'object' ? r.data : r) as Rec
  const nestedData = payload.data && typeof payload.data === 'object' ? (payload.data as Rec) : undefined
  const source = nestedData ?? payload
  const hasError =
    r.success === false ||
    payload.success === false ||
    source.success === false ||
    source.status === 'error' ||
    source.status === 'failed' ||
    Boolean(
      r.error ??
        r.errorMessage ??
        r.detail ??
        r.details ??
        payload.error ??
        payload.errorMessage ??
        payload.detail ??
        payload.details ??
        source.error ??
        source.errorMessage ??
        source.detail ??
        source.details,
    )

  if (!hasError) return null

  const value =
    r.message ??
    r.error ??
    r.errorMessage ??
    r.detail ??
    r.details ??
    payload.message ??
    payload.explanation ??
    payload.error ??
    payload.errorMessage ??
    payload.detail ??
    payload.details ??
    source.message ??
    source.explanation ??
    source.error ??
    source.errorMessage ??
    source.detail ??
    source.details

  return value ? String(value) : 'O backend devolveu um erro ao validar os dados.'
}

/** The validator shows nothing while waiting; on a thrown request it shows this. */
export const VALIDATOR_REQUEST_ERROR = 'O backend devolveu um erro ao validar os dados.'

// ─── project-domain — verdict box ─────────────────────────────────────────────
// Upstream: fraud-mono-repo/project-domain/src/components/DomainCheckForm.tsx
//           formatVerdict. Last synced 2026-10-18. Reads is_scam / scam_claims /
//           explanation — never `translation`. The site's "Captura do site"
//           screenshot panel comes from a separate screenshot API, not from
//           the entry, and is not reproduced.

export function formatDomainVerdict(value: unknown): VerdictBoxData | null {
  const payload = unwrapBackendPayload(value)
  if (!payload) return null

  if (payload.classificationPending) {
    return {
      label: 'Análise em curso',
      message: 'Submissão recebida. O veredicto final ainda está em preparação.',
      items: [],
      tone: 'warning',
    }
  }

  const bkResult = getRecord(payload.bk_result)
  const claims = Array.isArray(payload.scam_claims) ? payload.scam_claims.map(String).filter(Boolean) : []
  const explanation =
    typeof payload.explanation === 'string' ? payload.explanation : typeof payload.message === 'string' ? payload.message : undefined
  const recipient =
    typeof bkResult?.beneficiario === 'string'
      ? bkResult.beneficiario
      : typeof bkResult?.paymentowner === 'string'
        ? bkResult.paymentowner
        : undefined
  const items = [recipient ? `Entidade indicada: ${recipient}` : null, ...claims].filter(Boolean).map(String).slice(0, 5)
  const fallbackMessage = explanation ?? items[0]

  if (payload.is_scam === true) {
    return {
      label: 'Provável fraude',
      message: fallbackMessage || 'Foram encontrados sinais fortes de fraude neste site.',
      items,
      tone: 'danger',
    }
  }

  if (payload.is_scam === false || payload.status === 'found') {
    return {
      label: 'Parece legítimo',
      message: fallbackMessage || 'Não foram encontrados sinais fortes de fraude nesta análise.',
      items,
      tone: 'safe',
    }
  }

  if (fallbackMessage || items.length > 0) {
    return {
      label: 'Resultado recebido',
      message: fallbackMessage ?? 'O serviço devolveu sinais adicionais.',
      items,
      tone: 'warning',
    }
  }

  return null
}

/** The domain checker's box while the request is in flight. */
export const DOMAIN_SUBMITTING: VerdictBoxData = { label: 'A analisar', message: 'Submissão em processamento.', items: [], tone: 'warning' }
